import {
  ClothingSizes,
  ImprovedParticipantShop,
} from "../../Interfaces/FundraiserRole";
import ColorPicker from "../../components/SingleProductPage/OptionChooser/ColorPicker";
import ButtonSelector from "../../components/SingleProductPage/OptionChooser/ButtonSelector";
import { Category, colorHexMap } from "../../CommonFunctions";
import { logoOverlay, textOverlay } from "./styles/custom_image_overlays";
import { useEffect } from "react";
interface StyleOptionSelectorProps {
  category: Category;
  selectedOption?: string;
  shopInfo: ImprovedParticipantShop;
  isOrg: boolean;
  setSelectedOption: (option: string) => void;
}

export const StyleOptionSelector: React.FC<StyleOptionSelectorProps> = ({
  category,
  selectedOption,
  setSelectedOption,
  shopInfo,
  isOrg,
}) => {
  return (
    <ButtonSelector
      options={getDynamicStyleOptions(category, shopInfo, isOrg)}
      selectedOption={selectedOption}
      setSelectedOption={setSelectedOption}
      optionDescription="Choose a Style:"
    />
  );

  return null; // fallback in case category doesn’t match
};

interface ClothingSizeSelectorProps {
  clothing_sizes: ClothingSizes;
  sku: string;
  selectedOption?: string;
  setSelectedOption: (option: string) => void;
}

export const ClothingSizeSelector: React.FC<ClothingSizeSelectorProps> = ({
  clothing_sizes,
  sku,
  selectedOption,
  setSelectedOption,
}) => {
  const optionsWithUpcharge: Record<string, string> = Object.fromEntries(
    (clothing_sizes[sku] || []).map((opt) => [
      opt.db_name,
      opt.up_charge > 0
        ? `${opt.display_name} (+$${opt.up_charge})`
        : opt.display_name,
    ]),
  );
  return (
    <ButtonSelector
      options={optionsWithUpcharge}
      selectedOption={selectedOption}
      setSelectedOption={setSelectedOption}
      optionDescription="Choose a Clothing Size:"
    />
  );
};

export type LogoCategory = keyof typeof logoOverlay;
export type TextCategory = keyof typeof textOverlay;

export const logoOverlayFetcher = (category: string, isMobile: boolean) => {
  const normalized = category.replace(/^org_/, "").replace(/_org_/, "_");
  const config = logoOverlay[normalized as LogoCategory];
  if (!config) return null;
  return isMobile ? config.mobile : config.desktop;
};

export const textOverlayFetcher = (category: string, isMobile: boolean) => {
  const normalized = category.replace(/^org_/, "").replace(/_org_/, "_");
  const config = textOverlay[normalized as TextCategory];
  if (!config) return null;
  return isMobile ? config.mobile : config.desktop;
};

export const getDynamicFontSize = (
  baseFontSize: number | undefined,
  text: string,
) => {
  const base_modifier = window.innerWidth > 768 ? 40 : 25; // different base modifiers for desktop/mobile
  const small_length = 6;
  if (!baseFontSize) return 16; // default font size if none provided
  if (text.length < small_length) {
    return baseFontSize * (base_modifier / small_length); // make a base size otherwise it stretches out too much
  }
  return baseFontSize * (base_modifier / text.length);
};

export const isBottleCategory = (category: Category) => {
  return (
    category === Category.CustomTumblers ||
    category === Category.OrganizationTumblers ||
    category === Category.Custom20ozBottles ||
    category === Category.Organization20ozBottles ||
    category === Category.CustomTravelMugs ||
    category === Category.OrganizationTravelMugs ||
    category === Category.Custom40ozBottles ||
    category === Category.Organization40ozBottles
  );
};

export const isClothingCategory = (category: Category) => {
  return (
    category === Category.CustomHoodies ||
    category === Category.CustomTShirts ||
    category === Category.CustomRacerbacks
  );
};

export const getStyleNameByCategory = (category: Category) => {
  if (category) return category;
  return "Style";
};

export const getDynamicStyleOptions = (
  category: Category,
  shopInfo: ImprovedParticipantShop,
  isOrg: boolean,
) => {
  const category_choices: Record<string, string> = {};

  if (isClothingCategory(category)) {
    Object.entries(shopInfo.sku_by_category).forEach(([cat, skus]) => {
      if (cat === Category.CustomHoodies && skus.length > 0) {
        category_choices["Custom Hoodies"] = "Hoodie";
      }
      if (cat === Category.CustomTShirts && skus.length > 0) {
        category_choices["Custom T-Shirts"] = "T-Shirt";
      }
      if (cat === Category.CustomRacerbacks && skus.length > 0) {
        category_choices["Custom Racerbacks"] = "Racerback";
      }
    });
  }
  if (isBottleCategory(category) && !isOrg) {
    Object.entries(shopInfo.sku_by_category).forEach(([cat, skus]) => {
      if (cat === Category.Custom20ozBottles && skus.length > 0) {
        category_choices["Custom 20oz Bottles"] = "20oz Bottle";
      }
      if (cat === Category.Custom40ozBottles && skus.length > 0) {
        category_choices["Custom 40oz Bottles"] = "40oz Bottle";
      }
      if (cat === Category.CustomTumblers && skus.length > 0) {
        category_choices["Custom Tumblers"] = "Tumblers";
      }
      if (cat === Category.CustomTravelMugs && skus.length > 0) {
        category_choices["Custom Travel Mugs"] = "Travel Mugs";
      }
    });
  }
  if (isBottleCategory(category) && isOrg) {
    Object.entries(shopInfo.sku_by_category).forEach(([cat, skus]) => {
      if (cat === Category.Organization20ozBottles && skus.length > 0) {
        category_choices["Organization 20oz Bottles"] = "20oz Bottle";
      }
      if (cat === Category.Organization40ozBottles && skus.length > 0) {
        category_choices["Organization 40oz Bottles"] = "40oz Bottle";
      }
      if (cat === Category.OrganizationTumblers && skus.length > 0) {
        category_choices["Organization Tumblers"] = "Tumblers";
      }
      if (cat === Category.OrganizationTravelMugs && skus.length > 0) {
        category_choices["Organization Travel Mugs"] = "Travel Mugs";
      }
    });
  }
  return category_choices;
};

export const getColorOptions = (
  category: Category,
  shopInfo: ImprovedParticipantShop,
) => {
  const colorOptions: string[] = [];
  return colorOptions;
};

interface ColorSelectorProps {
  shopInfo: ImprovedParticipantShop;
  selectedOption?: string;
  setSelectedOption: (option: string) => void;
  setSku: (sku: string) => void;
  selectedCategory: string;
}

export const ColorSelector: React.FC<ColorSelectorProps> = ({
  shopInfo,
  selectedOption,
  setSelectedOption,
  selectedCategory,
  setSku,
}) => {
  useEffect(() => {
    const firstSku = Object.keys(colorOptions)[0];
    if (firstSku) {
      const skuNum = Number(firstSku);
      setSelectedOption(colorOptions[skuNum]); // e.g. "Red"
      setSku(skuNum.toString()); // the SKU
    }
  }, [selectedCategory]);

  const colorOptions: Record<number, string> = {};
  shopInfo.sku_by_category[selectedCategory]?.forEach((sku) => {
    const product = shopInfo.products.find((p) => p.sku === sku);
    if (product?.name) {
      // Check if product name contains any of the colors in the map
      Object.keys(colorHexMap).forEach((colorName) => {
        const regex = new RegExp(`\\b${colorName}\\b`, "i");
        if (regex.test(product.name) && !colorOptions[product.sku]) {
          colorOptions[product.sku] = colorHexMap[colorName]
            ? colorName
            : "Other";
        }
      });
    }
  });
  return (
    <ColorPicker
      colors={colorOptions}
      selectedColor={selectedOption}
      setSelectedColor={setSelectedOption}
      setSku={setSku}
    />
  );
};
